import posthog from "posthog-js";
import { projectSelector } from "@rflocal/shared";
import { AnalyticsAbstractAdapter } from "./abstract";
import { AnalyticsMethod, AnalyticsEventData } from "../types";

function compactProperties(properties: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(properties).filter(([, value]) => value !== undefined && value !== null)
    );
}

function getWorkspaceGroupProperties(data: AnalyticsEventData): Record<string, unknown> {
    return compactProperties({
        name: data.workspaceName,
        url: data.workspaceUrl,
        plan: data.workspacePlan
    });
}

function getPersonProperties(data: AnalyticsEventData): Record<string, unknown> {
    const { workspaceId, workspaceUrl, workspaceName, workspacePlan, ...personProperties } = data;
    return compactProperties(personProperties);
}

export class PostHogAnalyticsAdapter extends AnalyticsAbstractAdapter {
    protected readonly enabledMethods: AnalyticsMethod[] = Object.values(AnalyticsMethod);

    protected postEvent({
        method,
        id,
        data
    }: {
        method: AnalyticsMethod;
        id?: string;
        data: AnalyticsEventData;
    }) {
        const posthogConfig = projectSelector.posthogConfig?.();
        if (!posthogConfig?.enabled || !posthogConfig.apiKey) {
            return;
        }

        if (projectSelector.isLocal()) {
            console.log(`🧷 [Analytics] ${this.adapterName}`, method, id, data);
            return;
        }

        if (method === AnalyticsMethod.PAGE) {
            return posthog.capture("$pageview", { ...data, page: id });
        }

        if (method === AnalyticsMethod.IDENTIFY) {
            if (!id) return;

            const result = posthog.identify(id, getPersonProperties(data));

            if (data.workspaceId) {
                const groupProperties = getWorkspaceGroupProperties(data);
                if (Object.keys(groupProperties).length === 0) {
                    posthog.group("workspace", data.workspaceId);
                } else {
                    posthog.group("workspace", data.workspaceId, groupProperties);
                }
            }

            return result;
        }

        if (!id) return;
        return posthog.capture(id, data);
    }
}
